import type * as React from "react";
import { cn } from "~/lib/utils";

type BaseProps = {
  children: React.ReactNode;
  className?: string;
};

type TitleProps = BaseProps & {
  as?: "h1" | "h2" | "h3" | "h4";
  size?: "hero" | "section" | "card";
};

export function Title({
  as: Comp = "h2",
  size = "section",
  className,
  children,
}: TitleProps) {
  return (
    <Comp
      className={cn(
        "font-serif tracking-tight text-body",
        size === "hero" && "text-[clamp(36px,6vw,68px)] leading-[1.08]",
        size === "section" && "text-[clamp(28px,4vw,46px)] leading-tight",
        size === "card" && "text-[14px] font-semibold leading-snug font-sans",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

type SubtitleProps = BaseProps & {
  align?: "left" | "center";
  maxWidth?: "none" | "sm" | "md";
};

export function Subtitle({
  children,
  className,
  align = "left",
  maxWidth = "none",
}: SubtitleProps) {
  return (
    <p
      className={cn(
        "text-[15px] leading-[1.65] text-secondary",
        align === "center" && "text-center",
        maxWidth === "sm" && "max-w-[460px]",
        maxWidth === "md" && "max-w-[540px]",
        className,
      )}
    >
      {children}
    </p>
  );
}

type ParagraphProps = BaseProps & {
  size?: "sm" | "md";
};

export function Paragraph({
  children,
  className,
  size = "md",
}: ParagraphProps) {
  return (
    <p
      className={cn(
        "text-secondary",
        size === "sm" && "text-[13px] leading-[1.55]",
        size === "md" && "text-[14px] leading-[1.65]",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function Eyebrow({ children, className }: BaseProps) {
  return (
    <div
      className={cn(
        "text-[11px] font-bold uppercase tracking-[0.10em] text-accent-dark",
        className,
      )}
    >
      {children}
    </div>
  );
}
